import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { format } from 'date-fns';
import { OverlayTrigger, Popover, Thumbnail } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import Truncate from 'react-truncate';
import { compose, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import 'slick-carousel/slick/slick.css';
import { contentStatus, routes, userContentCompletionStatus, dateFormats } from '../../../../constants';
import { getUserIndustryGoals, tileCpdCategoryClassName } from '../../../../services';
import { pluralise } from '../../../../utils';
import { Icon } from '../../../components';
import Tooltip from '../../../components/tooltip';
import { TooltipWrapper } from './tooltip-wrapper';
import bookmarkOutlined from '../../../../images/bookmark-outlined.svg';
import bookmarkFilled from '../../../../images/bookmark-filled.svg';
import { userActions } from '../../../../state/modules/user';

const overlay = (status, completionStatus) =>
  <Popover id='content-status-popover'>
    <p>
      {
        completionStatus === userContentCompletionStatus.Completed ?
          status === contentStatus.Archived.value ?
            'You completed this content but it has been archived. You still get the points, but it can no longer be viewed.' :
            'You completed this content but it is no longer available to view. Come back later to view this item or contact Caddie for more information.'
          :
          status === contentStatus.Archived.value ?
            'This content has been archived and is no longer available.' :
            'This content is not available to view. Come back later to view this item or contact Caddie for more information.'
      }
    </p>
  </Popover>

const ContentTile = ({
  cpdCategoryId,
  altText,
  category,
  fullCpdCategory,
  cpdPoints,
  dateTime,
  duration,
  icon,
  image,
  imageSet,
  publisher,
  title,
  description,
  venue,
  contentRoute,
  completionStatus,
  removeContentHandler,
  searchMode,
  selected,
  openInNewWindow,
  readOnly,
  isTasaCpd,
  status,
  onTruncate,
  truncated,
  goals,
  userIndustryGoals,
  goTo,
  expiryDate,
  isBookmarked,
  id,
  addUserBookmark,
  removeUserBookmark
}) => {
  const cpdCategoryIdParsed = parseInt(cpdCategoryId, 10)
  const goToCpdCategoryLibrary = cpdCategoryIdParsed ?
    () => goTo(routes.app.training.cpdCategory(cpdCategoryIdParsed)) :
    null
  return (
    <div onClick={searchMode && removeContentHandler} className={'tile' + (selected ? " selected" : " unselected")}>
      <header>
        <div className='title-header'>
          {
            completionStatus === userContentCompletionStatus.Completed && status === contentStatus.Published.value ?
              <div className='cpd-progress-banner completed'>
                Completed
              </div> : null
          }
          {
            completionStatus === userContentCompletionStatus.Failed && status === contentStatus.Published.value ?
              <div className='cpd-progress-banner failed'>
                Unsuccessful
              </div> : null
          }
          {
            completionStatus === userContentCompletionStatus.InProgress && status === contentStatus.Published.value ?
              <div className='cpd-progress-banner in-progress'>
                In Progress
              </div> : null
          }
          {
            completionStatus === userContentCompletionStatus.Tasked && status === contentStatus.Published.value ?
              <div className='cpd-progress-banner tasked'>
                Compulsory CPD
              </div> : null
          }
          {
            status !== contentStatus.Published.value ?
              <div className='cpd-progress-banner archived'>
                {status === contentStatus.Archived.value ? 'Archived' : 'Unavailable'}
              </div> : null
          }
          {
            selected && removeContentHandler ?
              <div onClick={removeContentHandler} className='task-remove-banner'>
                Selected<Icon className='right' iconSize={2} icon='close-thick' />
              </div> : null
          }
          {
            openInNewWindow ?
              searchMode ? <Thumbnail src={image} srcSet={imageSet} alt={altText} /> :
                <a href={contentRoute.pathname + contentRoute.search} target="_blank">
                  <Thumbnail src={image} srcSet={imageSet} alt={altText} />
                </a> :
              status === contentStatus.Published.value ?
                <LinkContainer to={contentRoute}>
                  <Thumbnail src={image} srcSet={imageSet} alt={altText} />
                </LinkContainer> :
                <OverlayTrigger placement='top' overlay={overlay(status, completionStatus)}>
                  <Thumbnail src={image} srcSet={imageSet} alt={altText} />
                </OverlayTrigger>
          }
        </div>
      </header>
      <main>
        <div className='category'>
          {category ? (
            <TooltipWrapper
              className={tileCpdCategoryClassName(category)}
              category={category}
              fullCpdCategory={fullCpdCategory}
              clickHandler={goToCpdCategoryLibrary}
            />
          ) : (
            <div className='publisher'>{publisher}</div>
          )}
          {
            fp.flow(
              fp.filter(
                ({ id }) => fp.contains(id)(goals)
              ),
              fp.map(
                ({ id, name }) => <span key={id} className='cat clickable' onClick={() => goTo(routes.app.training.goal(id))}>
                  {
                    fp.first(fp.split(' ')(name))
                  }
                </span>
              )
            )(userIndustryGoals)
          }
          <span className='icon'><Icon icon={icon} /></span>
          {
            isBookmarked !== undefined
            ? <img
                className={`bookmark-icon${ isBookmarked ? ' selected' : ''}`}
                src={isBookmarked ? bookmarkFilled : bookmarkOutlined}
                title={isBookmarked ? "Remove bookmark" : "Add bookmark"}
                alt="Content Bookmark"
                onClick={isBookmarked ? () => removeUserBookmark(id) : () => addUserBookmark(id)}
              />
            : null
          }
        </div>
        {category ? <div className='publisher'>{publisher}</div> : null}
        <div className='title'>
          {
            openInNewWindow ?
              searchMode ?
                <p>{title}</p> :
                <a href={contentRoute.pathname + contentRoute.search} target="_blank">{title}</a> :
              status === contentStatus.Published.value ?
                <Link to={contentRoute}>{title}</Link> :
                <OverlayTrigger placement='bottom' overlay={overlay(status, completionStatus)}>
                  <p>{title}</p>
                </OverlayTrigger>
          }
        </div>
        <span className='description' alt={description}>
          <Tooltip
            disabled={!truncated}
            placement='bottom'
            component={
              <Truncate onTruncate={onTruncate} lines={3} ellipsis={<a className='action'>...Read More</a>}>
                {description}
              </Truncate>
            } tip={<p className='padding-thin tip'>{description}</p>} />
        </span>
      </main>
      <footer>
        <span className='time'>{duration}</span>
        {
          cpdPoints ?
            <span className='points'>{`${cpdPoints} CPD ${pluralise('hour', cpdPoints)}`}</span> :
            <span className='points'>CPD n/a</span>
        }
        <span className='time'>Expiry date:</span>
        <span className='points'>{format(new Date(expiryDate), dateFormats.ShortDate)}</span>
      </footer>
    </div>
  );
}

ContentTile.propTypes = {
  altText: PropTypes.string,
  category: PropTypes.string,
  cpdPoints: PropTypes.number,
  duration: PropTypes.string,
  icon: PropTypes.string,
  image: PropTypes.string,
  imageSet: PropTypes.string,
  publisher: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.number,
  completionStatus: PropTypes.string,
  taskId: PropTypes.number,
  isTasaCpd: PropTypes.bool
};

const mapStateToProps = ({ user, industry }) => ({
  userIndustryGoals: getUserIndustryGoals(user.profile, fp.get('industriesWithCpdCategorys')(industry))
})

const mapDispatchToProps = (dispatch) => ({
  addUserBookmark: bindActionCreators(userActions.user.addUserBookmark, dispatch),
  removeUserBookmark: bindActionCreators(userActions.user.removeUserBookmark, dispatch),
  goTo: bindActionCreators(push, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ id, taskId, cpdPoints }) => ({
      contentRoute: {
        pathname: cpdPoints ? routes.app.training.content : routes.app.training.nonCpdContent,
        search: `?${queryString.stringify({ taskId: taskId, contentId: id })}`
      }
    })
  ),
  withStateHandlers(
    {
      truncated: false
    },
    {
      onTruncate: () => (truncated) => ({
        truncated
      })
    }
  )
)(ContentTile);



// WEBPACK FOOTER //
// ./src/containers/pages/app/partials/content-tile.js